import { useEffect, useMemo, useState } from "react";

import { ContainerTareMap } from "@/lib/containerTareMap";
import { readRateLookupCriteria } from "@/features/rates/rateLookupCriteria";
import { api } from "@/services/api";

/**
 * Cargo entered in Rate Lookup (widget / rates-page rail), plus the tare map
 * needed to turn it into per-line gross on the card table and side rail.
 */
export function useRateLookupCargoWeight() {
  const criteria = useMemo(() => readRateLookupCriteria(), []);
  const cargoKg = criteria.cargo_kg ?? null;
  // LCL cargo is priced per revenue ton (W/M) and shares a container, so the
  // empty-container tare does not apply — never add an FCL tare for LCL.
  const isLcl = criteria.load_type === "lcl";
  const [tareMap, setTareMap] = useState(() => ContainerTareMap.fromApiRows([]));

  useEffect(() => {
    if (cargoKg == null || isLcl) return;
    let cancelled = false;
    void api
      .getContainerTares()
      .then((rows) => {
        if (!cancelled) setTareMap(ContainerTareMap.fromApiRows(rows));
      })
      .catch(() => {
        if (!cancelled) setTareMap(ContainerTareMap.fromApiRows([]));
      });
    return () => {
      cancelled = true;
    };
  }, [cargoKg, isLcl]);

  const resolveGrossKg = useMemo(() => {
    if (cargoKg == null) return () => null;
    // For LCL the gross equals the cargo weight (no container tare).
    if (isLcl) return () => cargoKg;
    return (
      carrier: string | null | undefined,
      equipmentSize: string | null | undefined,
      equipmentType: string | null | undefined,
    ): number | null => {
      // An LCL line has no container tare even inside an otherwise-FCL view.
      if (equipmentType?.trim().toUpperCase() === "LCL") return cargoKg;
      const { tareKg } = tareMap.lookup(
        carrier,
        equipmentSize || "40",
        equipmentType || "DV",
      );
      return cargoKg + tareKg;
    };
  }, [cargoKg, isLcl, tareMap]);

  return { cargoKg, resolveGrossKg };
}
